import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, X } from 'lucide-react';
import { recruitmentClientApi } from '@/lib/recruitmentApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const EDU = ['high_school', 'bachelor', 'master', 'phd', 'other'];
const URG = ['low', 'medium', 'high', 'critical'];
const MOD = ['remote', 'onsite', 'hybrid'];
const LANGS = ['spanish', 'english', 'french', 'portuguese', 'other'];

function TagInput({ value = [], onChange, placeholder, testid }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {value.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-1">
            {tag}
            <button
              type="button"
              className="hover:text-destructive"
              onClick={() => onChange(value.filter((x) => x !== tag))}
              aria-label="remove"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={placeholder}
      />
    </div>
  );
}

export function IntakeFormDialog({ open, onOpenChange, processId, defaultPosition, defaultDepartment, prefill, onSubmitted }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    position_title: defaultPosition || '',
    department: defaultDepartment || '',
    key_responsibilities: '',
    required_competencies: [],
    salary_range: '',
    years_experience: '',
    education_level: 'bachelor',
    soft_skills: [],
    languages: [],
    certifications: '',
    urgency: 'medium',
    work_modality: 'hybrid',
    reporting_line: '',
    number_of_openings: 1,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        ...(prefill || {}),
        position_title: prefill?.position_title || defaultPosition || f.position_title,
        department: prefill?.department || defaultDepartment || f.department,
      }));
    }
  }, [open, defaultPosition, defaultDepartment, prefill]);

  const set = (k) => (v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleLang = (l) => () =>
    setForm((prev) => ({
      ...prev,
      languages: prev.languages.includes(l)
        ? prev.languages.filter((x) => x !== l)
        : [...prev.languages, l],
    }));

  const submit = async () => {
    if (!form.position_title.trim() || !form.department.trim() ||
        !form.key_responsibilities.trim() || !form.number_of_openings) {
      toast.error(t('recruitment.intake.validationRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_experience: form.years_experience === '' ? null : Number(form.years_experience),
        number_of_openings: Number(form.number_of_openings),
        salary_range: form.salary_range || null,
        certifications: form.certifications || null,
        reporting_line: form.reporting_line || null,
      };
      await recruitmentClientApi.submitIntake(processId, payload);
      toast.success(t('recruitment.intake.submitted'));
      onSubmitted && onSubmitted();
      onOpenChange(false);
    } catch (e) {
      toast.error(t('recruitment.intake.submitFailed'), { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onOpenChange(false)}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="intake-form-dialog">
        <DialogHeader>
          <DialogTitle>{t('recruitment.intake.title')}</DialogTitle>
          <DialogDescription>{t('recruitment.intake.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4 py-1">
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.positionTitle')} *</Label>
            <Input data-testid="intake-position" value={form.position_title} onChange={(e) => set('position_title')(e.target.value)} placeholder={t('recruitment.intake.fields.positionTitlePh')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.department')} *</Label>
            <Input data-testid="intake-department" value={form.department} onChange={(e) => set('department')(e.target.value)} placeholder={t('recruitment.intake.fields.departmentPh')} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('recruitment.intake.fields.keyResponsibilities')} *</Label>
            <Textarea rows={3} data-testid="intake-responsibilities" value={form.key_responsibilities} onChange={(e) => set('key_responsibilities')(e.target.value)} placeholder={t('recruitment.intake.fields.keyResponsibilitiesPh')} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('recruitment.intake.fields.requiredCompetencies')}</Label>
            <TagInput
              testid="intake-competencies"
              value={form.required_competencies}
              onChange={set('required_competencies')}
              placeholder={t('recruitment.intake.fields.requiredCompetenciesPh')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.salaryRange')}</Label>
            <Input data-testid="intake-salary" value={form.salary_range} onChange={(e) => set('salary_range')(e.target.value)} placeholder={t('recruitment.intake.fields.salaryRangePh')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.yearsExperience')}</Label>
            <Input
              data-testid="intake-years"
              type="number" min="0" max="80"
              value={form.years_experience}
              onChange={(e) => set('years_experience')(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.educationLevel')}</Label>
            <Select value={form.education_level} onValueChange={set('education_level')}>
              <SelectTrigger data-testid="intake-education"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EDU.map((e) => (
                  <SelectItem key={e} value={e}>{t(`recruitment.intake.educationOpts.${e}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.urgency')}</Label>
            <Select value={form.urgency} onValueChange={set('urgency')}>
              <SelectTrigger data-testid="intake-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {URG.map((u) => (
                  <SelectItem key={u} value={u}>{t(`recruitment.intake.urgencyOpts.${u}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('recruitment.intake.fields.softSkills')}</Label>
            <TagInput
              testid="intake-softskills"
              value={form.soft_skills}
              onChange={set('soft_skills')}
              placeholder={t('recruitment.intake.fields.softSkillsPh')}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('recruitment.intake.fields.languages')}</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="intake-languages">
              {LANGS.map((l) => (
                <label key={l} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.languages.includes(l)}
                    onCheckedChange={toggleLang(l)}
                    data-testid={`intake-lang-${l}`}
                  />
                  <span className="text-sm">{t(`recruitment.intake.languageOpts.${l}`)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.certifications')}</Label>
            <Input data-testid="intake-certs" value={form.certifications} onChange={(e) => set('certifications')(e.target.value)} placeholder={t('recruitment.intake.fields.certificationsPh')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.workModality')}</Label>
            <Select value={form.work_modality} onValueChange={set('work_modality')}>
              <SelectTrigger data-testid="intake-modality"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MOD.map((m) => (
                  <SelectItem key={m} value={m}>{t(`recruitment.intake.modalityOpts.${m}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.reportingLine')}</Label>
            <Input data-testid="intake-reporting" value={form.reporting_line} onChange={(e) => set('reporting_line')(e.target.value)} placeholder={t('recruitment.intake.fields.reportingLinePh')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.intake.fields.numberOfOpenings')} *</Label>
            <Input
              data-testid="intake-openings"
              type="number" min="1" max="500"
              value={form.number_of_openings}
              onChange={(e) => set('number_of_openings')(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="intake-submit"
            onClick={submit}
            disabled={submitting}
            className="bg-gradient-primary hover:opacity-90"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('recruitment.intake.submitting')}</>
            ) : t('recruitment.intake.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default IntakeFormDialog;
